import React, { useRef, useState } from 'react'
import {
  Button,
  Dropdown,
  Input,
  Notification,
  NotificationType,
  Content,
  Textarea
} from '@ampcore/adel-react'
import xss from 'xss'
import StatesData from '../views/usa-states.json'
import { isValidEmail, isValidZipcode } from '../../../common/utils'
import { CONSTs, formSelectedData } from '../views/utils'

export type BrokerageFieldName =
  | 'firstname'
  | 'lastname'
  | 'emailaddress'
  | 'street'
  | 'city'
  | 'state'
  | 'zipcode'
  | 'inquiry'

export type BrokerageFormData = {
  firstname: string | null
  lastname: string | null
  emailaddress: string | null
  street: string | null
  city: string | null
  state: string | null
  zipcode: string | null
  inquiry: string | null
}

export type BrokerageErrorFields = {
  firstname: boolean
  lastname: boolean
  emailaddress: boolean
  state: boolean
  city: boolean
  street: boolean
  zipcode: boolean
  inquiry: boolean
  emailWrongFormat: boolean
}

export type BrokerageFormLabels = {
  firstname: string
  lastname: string
  emailaddress: string
  street: string
  city: string
  state: string
  zipcode: string
  inquiry: string
  buttonlabel: string
  buttonLoadingLabel: string
}

export type BrokerageFormErrorMessages = {
  errorFirstname: string
  errorLastname: string
  errorEmailaddress: string
  errorZipcode: string
  errorEmailaddressWrongFormat: string
}

export type BrokerageServiceMessages = {
  serviceSuccess: string
  serviceSuccessThankyou: string
  serviceFailed: string
  serviceCrossDomain?: string
}

export type BrokerageFormHeading = {
  title: string
}

export type BrokerageFormPleaseNote = {
  message: string
}

export type BrokerageFormEmailInfo = {
  subject: string
  toEmailAddress: string
  emailApi: string
}

export type BrokerageFormTemplate = {
  name: string
}

export type ContactFormBrokerageContent = {
  formEmailInfo: BrokerageFormEmailInfo
  formTemplate?: BrokerageFormTemplate
  formErrorMessages: BrokerageFormErrorMessages
  formPleaseNote?: BrokerageFormPleaseNote
  serviceMessages: BrokerageServiceMessages
  formHeading?: BrokerageFormHeading
  formLabels: BrokerageFormLabels
}

export type BrokerageCallback = (
  items: unknown,
  formEmailInfo: BrokerageFormEmailInfo,
  showServerResponse: (props: unknown, message?: string) => boolean
) => unknown

export type ContactFormBrokerageProps = {
  callback?: BrokerageCallback
  name?: string
  content: ContactFormBrokerageContent
  testId?: string
}

const ContactFormBrokerage = (props: ContactFormBrokerageProps) => {
  const { callback, name, content, testId } = props
  const [showServiceFailedMessage, setShowServiceFailedMessage] =
    useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<BrokerageFormData>({
    firstname: null,
    lastname: null,
    emailaddress: null,
    street: null,
    city: null,
    state: null,
    zipcode: null,
    inquiry: null
  })
  const [errorFields, setErrorFields] = useState<BrokerageErrorFields>({
    firstname: false,
    lastname: false,
    emailaddress: false,
    state: false,
    city: false,
    street: false,
    zipcode: false,
    inquiry: false,
    emailWrongFormat: false
  })

  const {
    formErrorMessages,
    formPleaseNote,
    serviceMessages,
    formHeading,
    formLabels
  } = content

  const firstNameRef = useRef<HTMLInputElement | null>(null)
  const lastNameRef = useRef<HTMLInputElement | null>(null)
  const emailAddressRef = useRef<HTMLInputElement | null>(null)
  const streetRef = useRef<HTMLInputElement | null>(null)
  const cityRef = useRef<HTMLInputElement | null>(null)
  const stateRef = useRef<HTMLSelectElement | null>(null)
  const zipcodeRef = useRef<HTMLInputElement | null>(null)
  const inquiryRef = useRef<HTMLTextAreaElement | null>(null)

  const showServerResponse = (props: unknown, message?: string): boolean => {
    if (props && message) {
      if (message === CONSTs.SUCCESS) {
        setLoading(false)
        setShowSuccessMessage(true)
        setShowServiceFailedMessage(false)
      } else {
        setLoading(false)
        setShowSuccessMessage(false)
        setShowServiceFailedMessage(true)
      }
    }
    return true
  }

  const getFormStatus = () => {
    let isFormValid = true

    for (const prop in formData) {
      const key = prop as keyof BrokerageFormData
      if (
        key !== 'state' &&
        key !== 'city' &&
        key !== 'street' &&
        key !== 'inquiry' &&
        formData[key] === null
      ) {
        isFormValid = false
      }
    }

    return isFormValid
  }

  const handleSelectState = (value: string) => {
    setFormData(prevState => ({
      ...prevState,
      state: value || null
    }))
    validateField('state', value)
  }

  const validateField = (fieldName: BrokerageFieldName, value: string) => {
    let emailValid = errorFields.emailaddress
    let firstnameValid = errorFields.firstname
    let lastnameValid = errorFields.lastname
    let zipcodeValid = errorFields.zipcode
    const fieldValues: BrokerageFormData = { ...formData }
    const fieldErrors: BrokerageErrorFields = { ...errorFields }

    switch (fieldName) {
    case 'firstname':
      firstnameValid = value !== '' || value.length > 0
      fieldValues.firstname = firstnameValid ? value : null
      fieldErrors.firstname = !firstnameValid
      break
    case 'lastname':
      lastnameValid = value !== '' || value.length > 0
      fieldValues.lastname = lastnameValid ? value : null
      fieldErrors.lastname = !lastnameValid
      break
    case 'emailaddress':
      emailValid = isValidEmail(value)
      fieldValues.emailaddress = emailValid ? value : null
      fieldErrors.emailaddress = !emailValid
      fieldErrors.emailWrongFormat = false
      {
        const formEmailAddress = emailAddressRef.current?.value || ''
        if (formEmailAddress && formEmailAddress.length > 0) {
          fieldErrors.emailWrongFormat = true
        }
      }
      break
    case 'zipcode':
      zipcodeValid = isValidZipcode(value)
      fieldValues.zipcode = zipcodeValid ? value : null
      fieldErrors.zipcode = !zipcodeValid
      break
    default:
      fieldValues[fieldName] = value || null
      break
    }
    setFormData(fieldValues)
    setErrorFields(fieldErrors)
  }

  const handleFieldChange = (event: React.SyntheticEvent<HTMLElement>) => {
    event.preventDefault()
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null

    if (!target || !target.name) {
      return
    }

    const fieldName = target.name as BrokerageFieldName
    const value = target.value

    setFormData(prevState => ({
      ...prevState,
      [fieldName]: value || null
    }))
    validateField(fieldName, value)

    if ('keyCode' in event && event.keyCode === 13) {
      validateField(fieldName, value)
    }
  }

  const submitData = (event: { preventDefault: () => void }) => {
    event.preventDefault()
    const { formEmailInfo, formTemplate } = content

    const canFormBeSubmitted = getFormStatus()

    if (canFormBeSubmitted) {
      setLoading(true)
      const items = formSelectedData(formData, formTemplate)
      if (callback && typeof callback === 'function' && items) {
        return callback(items, formEmailInfo, showServerResponse)
      }
      return true
    }

    const formFirstName = firstNameRef.current?.value || ''
    const formLastName = lastNameRef.current?.value || ''
    const formEmailAddress = emailAddressRef.current?.value || ''
    const formZipCode = zipcodeRef.current?.value || ''
    const fieldValues: BrokerageFormData = { ...formData }
    const fieldErrors: BrokerageErrorFields = { ...errorFields }

    if (formFirstName.length < 1) {
      fieldValues.firstname = null
      fieldErrors.firstname = true
    } else {
      fieldValues.firstname = formData.firstname
      fieldErrors.firstname = false
    }

    if (formLastName.length < 1) {
      fieldValues.lastname = null
      fieldErrors.lastname = true
    } else {
      fieldValues.lastname = formData.lastname
      fieldErrors.lastname = false
    }

    if (formEmailAddress || !formEmailAddress.length) {
      const cleanedText = formEmailAddress.toLowerCase()
      fieldValues.emailaddress = isValidEmail(cleanedText)
        ? formEmailAddress
        : null
      fieldErrors.emailaddress = !fieldValues.emailaddress
    }

    if (!formZipCode || formZipCode.length > 0) {
      fieldValues.zipcode = isValidZipcode(formZipCode) ? formZipCode : null
      fieldErrors.zipcode = !isValidZipcode(formZipCode)
    }

    setFormData(fieldValues)
    setErrorFields(fieldErrors)

    return false
  }

  const setEmailErrorMessage = (
    wrongEmail: boolean,
    message: BrokerageFormErrorMessages
  ) => {
    if (wrongEmail) {
      return message.errorEmailaddressWrongFormat
    }

    return message.errorEmailaddress
  }

  return (
    <div className="u-marginTop32">
      {showSuccessMessage &&
        serviceMessages &&
        serviceMessages.serviceSuccess && (
        <Notification
          type={NotificationType.CONFIRM}
          dismissable={false}
          className="BrokerageFormNotification u-sizeConstrained"
          children={
            <>
              <h3 className="u-paddingBottom16">
                {serviceMessages.serviceSuccessThankyou}
              </h3>
              <p
                className="u-paddingTop16 u-paddingBottom16"
                dangerouslySetInnerHTML={{
                  __html: xss(serviceMessages.serviceSuccess)
                }}
              />
            </>
          }
        />
      )}
      {!showSuccessMessage && (
        <div className="u-sizeConstrained">
          <section
            className="u-marginBottom24 u-size5of12 u-md-size8of12 u-sm-sizeFull"
            onBlur={handleFieldChange}
            onKeyUp={handleFieldChange}
            data-form-name={name}
          >
            {formHeading && formHeading.title && (
              <h2 className="u-marginBottom24">{formHeading.title}</h2>
            )}
            <Input
              name="firstname"
              id="firstname"
              placeholder=""
              label={formLabels.firstname}
              reference={firstNameRef}
              error={errorFields.firstname}
              message={formErrorMessages.errorFirstname}
              testId={testId ? `${testId}-firstname` : undefined}
            />
            <Input
              name="lastname"
              id="lastname"
              placeholder=""
              label={formLabels.lastname}
              reference={lastNameRef}
              error={errorFields.lastname}
              message={formErrorMessages.errorLastname}
              testId={testId ? `${testId}-lastname` : undefined}
            />
            <Input
              name="emailaddress"
              id="email"
              placeholder=""
              label={formLabels.emailaddress}
              reference={emailAddressRef}
              error={errorFields.emailaddress}
              message={setEmailErrorMessage(
                errorFields.emailWrongFormat,
                formErrorMessages
              )}
              testId={testId ? `${testId}-email` : undefined}
            />
            <Input
              name="street"
              id="street"
              placeholder=""
              label={formLabels.street}
              reference={streetRef}
              testId={testId ? `${testId}-street` : undefined}
            />
            <Input
              name="city"
              id="city"
              placeholder=""
              label={formLabels.city}
              reference={cityRef}
              testId={testId ? `${testId}-city` : undefined}
            />
            <Dropdown
              name="state"
              id="state"
              placeholder="Select State"
              label={formLabels.state}
              reference={stateRef}
              callback={handleSelectState}
              data={StatesData}
              className={'u-marginBottom20'}
            />
            <Input
              name="zipcode"
              id="zipcode"
              placeholder=""
              label={formLabels.zipcode}
              reference={zipcodeRef}
              error={errorFields.zipcode}
              message={formErrorMessages.errorZipcode}
              testId={testId ? `${testId}-zipcode` : undefined}
            />
            <Textarea
              name="inquiry"
              id="inquiry"
              placeholder=""
              label={formLabels.inquiry}
              reference={inquiryRef}
              row={3}
            />
            {showServiceFailedMessage && serviceMessages.serviceFailed && (
              <Notification
                type={NotificationType.ALERT}
                message={serviceMessages.serviceFailed}
                dismissable={false}
                className="u-marginBottom24"
              />
            )}
            <Button
              onClick={submitData}
              isLoading={loading}
              data-analytics={'Button'}
              data-tracking={'Button'}
              children={
                loading ? formLabels.buttonLoadingLabel : formLabels.buttonlabel
              }
            />
          </section>
          {formPleaseNote && formPleaseNote.message && (
            <Content className={'u-marginBottom24'}>
              <div
                className="Content"
                dangerouslySetInnerHTML={{
                  __html: xss(formPleaseNote.message)
                }}
              />
            </Content>
          )}
        </div>
      )}
    </div>
  )
}

export default ContactFormBrokerage
